import type { PerformancePerson } from '@gokrew/models/performance';
import dayjs from 'dayjs';
import type {
  MyTeamCalendarResponse,
  MyTeamManagerRef,
  MyTeamMember,
  TeamTimeOffRow,
} from '@gokrew/models/my-team';
import type { CompanyLeaveCalendarResponse } from '@gokrew/models/leave-management';

/**
 * `PersonBadge` (shared initials avatar + name) takes a performance person;
 * a team member carries the same name and title, and nothing else is read.
 */
export const toBadgePerson = (
  member: Pick<MyTeamMember, 'employeeId' | 'firstName' | 'lastName'> & {
    designation?: MyTeamMember['designation'];
    department?: MyTeamMember['department'];
  }
): PerformancePerson => ({
  id: member.employeeId,
  userId: '',
  firstName: member.firstName,
  lastName: member.lastName ?? '',
  email: null,
  profileImage: null,
  designation: member.designation?.name ?? null,
  department: member.department?.name ?? null,
});

export const managerName = (manager: MyTeamManagerRef | null) =>
  manager ? `${manager.firstName} ${manager.lastName ?? ''}`.trim() : '';

/**
 * The team calendar has the company calendar's shape minus the reason and
 * the email, which a manager never receives (D4, D20). The shared calendar
 * hook takes the company shape, so the two fields are filled with blanks
 * rather than values.
 */
export const toCompanyCalendar = (
  response?: MyTeamCalendarResponse
): CompanyLeaveCalendarResponse | undefined => {
  if (!response) return undefined;
  const leaves = Object.fromEntries(
    Object.entries(response.data.leaves).map(([date, entries]) => [
      date,
      entries.map((entry) => ({
        ...entry,
        reason: '',
        startSession: entry.startSession ?? undefined,
        endSession: entry.endSession ?? undefined,
        startTime: entry.startTime ?? undefined,
        endTime: entry.endTime ?? undefined,
        employee: {
          ...entry.employee,
          lastName: entry.employee.lastName ?? '',
          email: '',
        },
      })),
    ])
  );
  return {
    message: response.message,
    data: {
      startDate: response.data.startDate,
      endDate: response.data.endDate,
      holidays: response.data.holidays.map((holiday) => ({
        ...holiday,
        description: holiday.description ?? '',
      })),
      leaves,
    },
  };
};

/**
 * How much of one day a leave takes: the whole day, the morning, the
 * afternoon or a few hours. A leave that starts in the afternoon (`PM`) or
 * ends at noon (`AM`) is a half day on that edge only.
 */
export type TeamDayPart = 'full' | 'am' | 'pm' | 'hours';

export interface TeamDayEntry {
  row: TeamTimeOffRow;
  part: TeamDayPart;
}

export interface TeamDay {
  /** `yyyy-MM-dd`. */
  date: string;
  entries: TeamDayEntry[];
}

const dayPart = (row: TeamTimeOffRow, date: string): TeamDayPart => {
  if (row.startTime) return 'hours';
  if (date === row.startDate && row.startSession === 'PM') return 'pm';
  if (date === row.endDate && row.endSession === 'AM') return 'am';
  return 'full';
};

/**
 * "Who's off", one entry per person per working day between `from` and `to`
 * (both `yyyy-MM-dd`). Weekly days off (`weeklyHolidays`, 0 = Sunday) are
 * skipped so a Friday-to-Monday leave does not list the weekend. Days with
 * nobody off are left out; the rows' order (by start date, then name) is kept.
 */
export const groupTimeOffByDay = (
  rows: TeamTimeOffRow[],
  from: string,
  to: string,
  weeklyHolidays: number[]
): TeamDay[] => {
  const byDate = new Map<string, TeamDayEntry[]>();
  const last = dayjs(to);
  for (const row of rows) {
    const start = dayjs(row.startDate > from ? row.startDate : from);
    const end = dayjs(row.endDate < to ? row.endDate : to);
    for (let day = start; !day.isAfter(end); day = day.add(1, 'day')) {
      if (day.isAfter(last)) break;
      if (weeklyHolidays.includes(day.day())) continue;
      const date = day.format('YYYY-MM-DD');
      const entries = byDate.get(date) ?? [];
      // One line per person and day, even with two leaves on the same day.
      if (!entries.some((entry) => entry.row.employeeId === row.employeeId)) {
        entries.push({ row, part: dayPart(row, date) });
      }
      byDate.set(date, entries);
    }
  }
  return [...byDate.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, entries]) => ({ date, entries }));
};
